import { useCallback, useEffect, useState } from "react";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Receipt, Plus } from "@phosphor-icons/react";
import { PoStatusPill, money, fmtDT, Spinner } from "./stockUtils";
import { PartsPoFormDialog } from "./PartsPoFormDialog";
import { PartsPoDetailDialog } from "./PartsPoDetailDialog";

const CHIPS = [["", "All"], ["draft", "Draft"], ["sent", "Sent"], ["part_received", "Part received"], ["received", "Received"], ["cancelled", "Cancelled"]];

export function PartsPosTab({ options, refreshKey, onChanged, canEdit, openPoId, onPoOpened, openAdd, onAddHandled }) {
  const [pos, setPos] = useState(null);
  const [status, setStatus] = useState("");
  const [parts, setParts] = useState([]);
  const [form, setForm] = useState({ open: false, po: null });
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => API.get("/stock/pos", { params: status ? { status } : {} }).then((r) => setPos(r.data.pos)).catch(() => setPos([])), [status]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { API.get("/stock/parts").then((r) => setParts(r.data.parts)).catch(() => {}); }, [refreshKey]);
  useEffect(() => { if (openPoId) { setDetail(openPoId); onPoOpened?.(); } }, [openPoId, onPoOpened]);
  useEffect(() => { if (openAdd) { setForm({ open: true, po: null }); onAddHandled?.(); } }, [openAdd, onAddHandled]);
  const changed = () => { load(); onChanged?.(); };

  return (
    <div className="space-y-3" data-testid="stock-pos-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">{CHIPS.map(([v, l]) => <button key={v} type="button" onClick={() => setStatus(v)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${status === v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`pos-chip-${v || "all"}`}>{l}</button>)}</div>
        {canEdit && <Button size="sm" onClick={() => setForm({ open: true, po: null })} data-testid="pos-new"><Plus size={14} className="mr-1" /> New parts PO</Button>}
      </div>
      {pos === null ? <Spinner /> : pos.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-border rounded-lg" data-testid="pos-empty">
          <Receipt size={36} className="mx-auto text-muted-foreground mb-2" />
          <p className="font-semibold text-sm">No parts purchase orders{status ? " with this status" : ""}</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">Raise them from the Reorder tab when stock runs low, or start one here. Sending emails the supplier a branded PO PDF; receiving books the goods into stock.</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="pos-table">
            <thead><tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground"><th className="text-left px-4 py-3 font-semibold">PO</th><th className="text-left px-4 py-3 font-semibold">Supplier</th><th className="text-left px-4 py-3 font-semibold">Deliver to</th><th className="text-right px-4 py-3 font-semibold">Lines</th><th className="text-right px-4 py-3 font-semibold">Total</th><th className="text-left px-4 py-3 font-semibold">Status</th><th className="text-left px-4 py-3 font-semibold">Raised</th></tr></thead>
            <tbody className="divide-y divide-border">
              {pos.map((p) => (
                <tr key={p.id} className="row-hover cursor-pointer" onClick={() => setDetail(p.id)} data-testid={`po-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono font-medium">{p.po_number}</td>
                  <td className="px-4 py-3">{p.supplier_name || <span className="text-muted-foreground">Unassigned</span>}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{p.deliver_to_location_name || "—"}</td>
                  <td className="px-4 py-3 text-right">{p.lines.length}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">{money(p.total)}</td>
                  <td className="px-4 py-3"><PoStatusPill status={p.status} testid={`po-status-${p.id}`} /></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDT(p.created_at)} · {p.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PartsPoFormDialog open={form.open} po={form.po} options={options} parts={parts} onClose={() => setForm({ open: false, po: null })} onSaved={(po) => { changed(); if (!form.po) setDetail(po.id); }} />
      <PartsPoDetailDialog poId={detail} options={options} canEdit={canEdit} onClose={() => setDetail(null)} onChanged={changed} onEdit={(po) => { setDetail(null); setForm({ open: true, po }); }} />
    </div>
  );
}
